import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import moment from 'lib/moment-timezone-custom'
import classNames from 'classnames'
import PlayersCountDropdown from './schedule-slot/players-count-dropdown'
import PricingDropdown from './schedule-slot/pricing-dropdown'
import Body from './schedule-slot/body'
import SlotElement from './schedule-slot/slot-element'

function findMatchedCartGame (cartItems, room, time) {
  return cartItems && cartItems[`${time.valueOf()}_${room.id}`]
}

// maybe this can be done through just click event
function detectIsActive (selectedRoom, selectedTime, room, time) {
  return (
    selectedRoom &&
      selectedRoom.id === room?.id &&
      selectedTime &&
      selectedTime.diff(time) === 0
  )
}

function findPrice (timeSlot, playersCount) {
  const { status, prices } = timeSlot

  if (status !== 'available' || !prices) return [null, null]

  let index = -1
  if (prices.length === 1) {
    index = 0
  } else {
    index = prices.findIndex(price => price.playersCount === playersCount)
  }

  if (index >= 0) {
    return [prices[index].price, prices[index].originalPrice]
  }

  return [null, null]
}

export default function ScheduleSlot (
  {
    timeSlot, disabled = false, hidePrice = false, cartItems, selectedRoom, selectedTime,
    playersCount, onSelect, onPlayersCountSelect, onRemove
  }
) {
  const { room, type, time, status } = timeSlot
  const pastTime = time < moment()
  const matchedCartGame =
    useMemo(() => findMatchedCartGame(cartItems, room, time), [cartItems, room, time])

  const active =
    useMemo(
      () => detectIsActive(selectedRoom, selectedTime, room, time),
      [selectedRoom, selectedTime, room, time]
    )

  const [price, originalPrice] =
    useMemo(
      () => findPrice(timeSlot, playersCount),
      [timeSlot, playersCount]
    )

  const [{ working, playersCountDropdownOpen }, setState] =
    useState({ playersCountDropdownOpen: false, working: false })

  const handleSelect = ({ playersCount }) => {
    if (disabled || working) return

    if (!matchedCartGame && (!playersCount || !price)) {
      if (!playersCount && timeSlot.prices?.length === 1) {
        playersCount = timeSlot.prices[0].playersCount
        setState(state => ({ ...state, working: true }))
      } else {
        setState(state => ({ ...state, playersCountDropdownOpen: true }))
      }
    }

    onSelect({
      timeSlot,
      playersCount,
      game: matchedCartGame,
      complete: () => setState(state => ({ ...state, working: false }))
    })
  }

  const handlePlayersCountSelect = (playersCount) => {
    setState(state => ({ ...state, playersCountDropdownOpen: false }))
    onPlayersCountSelect(playersCount, () => { handleSelect({ playersCount }) })
  }

  const handleRemove = () => {
    if (!onRemove) return

    onRemove({
      game: matchedCartGame,
      timeSlot,
      complete: () => { setState(state => ({ ...state, working: false })) }
    })
  }

  return (
    <div className="slot_wrap"
      onMouseLeave={() => setState(state => ({ ...state, playersCountDropdownOpen: false }))}
    >
      <SlotElement
        matchedCartGame={matchedCartGame}
        onClick={() => handleSelect({ playersCount })}
        onRemove={handleRemove}
        className={
          classNames(
            'schedule_slot',
            type,
            {
              active,
              in_cart: matchedCartGame,
              confirm: active && playersCount,
              unavailable: status !== 'available',
              past_time: pastTime,
              disabled,
              discount: price && originalPrice && price < originalPrice
            }
          )
        }
      >
        {working
          ? (
          <div className="spinner" />
            )
          : (
          <Body
            {...{
              timeSlot, matchedCartGame, pastTime, priceProps: { hidePrice, price, originalPrice }
            }}
          />
            )}
      </SlotElement>
      {playersCountDropdownOpen
        ? (timeSlot.prices && timeSlot.prices.length > 0 && (
            <PricingDropdown
              isOpen={playersCountDropdownOpen}
              room={room}
              prices={timeSlot.prices}
              onSelect={handlePlayersCountSelect.bind(this)}
            />
          ))
        : (timeSlot.perPlayerPrice > 0 && (
            <PlayersCountDropdown
              isOpen={playersCountDropdownOpen}
              room={room}
              timeSlot={timeSlot}
              onSelect={handlePlayersCountSelect.bind(this)}
            />
          ))}
    </div>
  )
}

ScheduleSlot.propTypes = {
  onPlayersCountSelect: PropTypes.func,
  onSelect: PropTypes.func.isRequired,
  timeSlot: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  onRemove: PropTypes.func,
  cartItems: PropTypes.object,
  hidePrice: PropTypes.bool,
  selectedRoom: PropTypes.object,
  selectedTime: PropTypes.object,
  playersCount: PropTypes.number
}
